import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  IconArrowBackUp,
  IconCheck,
  IconHeart,
  IconHeartFilled,
  IconLoader2,
  IconX,
} from "@tabler/icons-react";
import { Button, Message } from "../../components";
import type { SongRequestItem, SongStatus } from "../../../services";
import { SongArt } from "./SongArt";

interface SongListProps {
  items: SongRequestItem[];
  isDj: boolean;
  djTab: "active" | "done" | "rejected";
  onVote: (item: SongRequestItem) => void;
  onStatus: (item: SongRequestItem, status: SongStatus) => Promise<void>;
}

export const SongList = ({
  items,
  isDj,
  djTab,
  onVote,
  onStatus,
}: SongListProps) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<Map<string, SongStatus>>(new Map());
  const inFlight = useRef<Set<string>>(new Set());

  const runStatus = async (item: SongRequestItem, status: SongStatus) => {
    // Synchronous guard so a fast double-tap can't fire two status writes; the
    // disabled attribute only lands after the next render.
    if (inFlight.current.has(item.id)) return;
    inFlight.current.add(item.id);
    setBusy((prev) => new Map(prev).set(item.id, status));
    try {
      await onStatus(item, status);
    } finally {
      inFlight.current.delete(item.id);
      setBusy((prev) => {
        const next = new Map(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  if (items.length === 0) {
    let emptyKey = "dj.empty";
    if (isDj) {
      if (djTab === "done") emptyKey = "dj.doneEmpty";
      else if (djTab === "rejected") emptyKey = "dj.rejectedEmpty";
      else emptyKey = "dj.activeEmpty";
    }
    return <Message variant="primary">{t(emptyKey)}</Message>;
  }

  return (
    <ul className="dj-list">
      {items.map((item) => {
        const itemBusy = busy.has(item.id);
        const busyStatus = busy.get(item.id);
        return (
          <li key={item.id} className="dj-song">
            <SongArt coverArt={item.coverArt} />
            <div className="dj-song-info">
              <span className="dj-song-title">{item.title}</span>
              <span className="dj-song-artist">
                {item.artist}
                {item.year ? ` · ${item.year}` : ""}
              </span>
            </div>
            <div className="dj-song-actions">
              {!isDj && (
                <button
                  type="button"
                  className={clsx("dj-vote", { liked: item.likedByMe })}
                  onClick={() => onVote(item)}
                  aria-label={t("dj.vote")}
                  aria-pressed={item.likedByMe}
                >
                  {item.likedByMe ? <IconHeartFilled /> : <IconHeart />}
                  <span className="dj-vote-count">{item.likeCount}</span>
                </button>
              )}
              {isDj && (
                <>
                  <span className="dj-song-count">
                    <IconHeart />
                    <span>{item.likeCount}</span>
                  </span>
                  {djTab === "active" ? (
                    <>
                      <Button
                        variant="success"
                        appearance="muted"
                        icon={
                          busyStatus === "played" ? (
                            <IconLoader2 className="dj-spin" />
                          ) : (
                            <IconCheck />
                          )
                        }
                        onClick={() => runStatus(item, "played")}
                        disabled={itemBusy}
                      >
                        {t("dj.confirm")}
                      </Button>
                      <Button
                        variant="error"
                        appearance="muted"
                        icon={
                          busyStatus === "rejected" ? (
                            <IconLoader2 className="dj-spin" />
                          ) : (
                            <IconX />
                          )
                        }
                        onClick={() => runStatus(item, "rejected")}
                        disabled={itemBusy}
                      >
                        {t("dj.reject")}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="secondary"
                      appearance="muted"
                      icon={
                        busyStatus === "pending" ? (
                          <IconLoader2 className="dj-spin" />
                        ) : (
                          <IconArrowBackUp />
                        )
                      }
                      onClick={() => runStatus(item, "pending")}
                      disabled={itemBusy}
                    >
                      {t("dj.restore")}
                    </Button>
                  )}
                </>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
